import clsx from "clsx";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "@/lib/api";
import type { BudgetStatus } from "@/lib/types";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-sm text-ink-3">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}…
    </div>
  );
}

export function EmptyState({ icon, title, body, action }: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-baseline px-6 py-12 text-center">
      {icon && <div className="text-ink-3">{icon}</div>}
      <div>
        <p className="font-semibold text-ink">{title}</p>
        {body && <p className="mx-auto mt-1 max-w-sm text-sm text-ink-3">{body}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof ApiError ? error.message : "Something went wrong.";
  const rid = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div role="alert" className="flex items-start gap-3 rounded-xl border border-border bg-surface p-4">
      <AlertCircle className="mt-0.5 size-5 shrink-0 text-status-critical" aria-hidden />
      <div className="text-sm">
        <p className="font-medium text-ink">{message}</p>
        {rid && <p className="mt-0.5 text-xs text-ink-3">Reference: {rid}</p>}
        {onRetry && (
          <button type="button" onClick={onRetry} className="mt-2 font-medium text-accent hover:underline">
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

const STATUS: Record<BudgetStatus | "good" | "critical" | "info", { icon: typeof Info; tone: string; label: string }> = {
  on_track: { icon: CheckCircle2, tone: "text-status-good", label: "On track" },
  under: { icon: CheckCircle2, tone: "text-status-good", label: "Under budget" },
  good: { icon: CheckCircle2, tone: "text-status-good", label: "Good" },
  warning: { icon: AlertTriangle, tone: "text-status-warning", label: "Watch" },
  over: { icon: XCircle, tone: "text-status-critical", label: "Over" },
  critical: { icon: XCircle, tone: "text-status-critical", label: "Problem" },
  upcoming: { icon: CircleDashed, tone: "text-ink-3", label: "Upcoming" },
  info: { icon: Info, tone: "text-accent", label: "Info" },
};

/** Status never relies on colour alone: icon + text label, always. */
export function StatusText({ status, children, className }: {
  status: keyof typeof STATUS;
  children?: ReactNode;
  className?: string;
}) {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <span className={clsx("inline-flex items-center gap-1.5 text-sm text-ink-2", className)}>
      <Icon className={clsx("size-4 shrink-0", s.tone)} aria-hidden />
      <span>{children ?? s.label}</span>
    </span>
  );
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={clsx("inline-flex items-center rounded-md bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-ink-2", className)}>
      {children}
    </span>
  );
}

// --- toasts ---------------------------------------------------------------------------------

interface Toast {
  id: number;
  message: string;
  tone: "success" | "error";
}
const ToastContext = createContext<(message: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const push = useCallback((message: string, tone: Toast["tone"] = "success") => {
    const id = next.current++;
    setToasts((t) => [...t, { id, message, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6">
        {toasts.map((t) => (
          <div key={t.id} role="status" className="pointer-events-auto flex items-center gap-2 rounded-lg bg-ink px-4 py-2.5 text-sm text-page shadow-lg">
            {t.tone === "success" ? (
              <CheckCircle2 className="size-4 text-status-good" aria-hidden />
            ) : (
              <XCircle className="size-4 text-status-critical" aria-hidden />
            )}
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong.";
}
